import { useEffect, useState } from "react";
import { FuturisticLoader } from "./FuturisticLoader";

export interface FullScreenLoaderProps {
  isLoading: boolean;
  messages?: string[];
  cycleSpeed?: number;
  minDisplayTime?: number;
  onLoadingComplete?: () => void;
}

export const FullScreenLoader: React.FC<FullScreenLoaderProps> = ({
  isLoading,
  messages = ["Loading..."],
  cycleSpeed = 3000,
  minDisplayTime = 1000,
  onLoadingComplete
}) => {
  const [currentMessageIndex, setCurrentMessageIndex] = useState(0);
  const [visible, setVisible] = useState(isLoading);
  const [startTime, setStartTime] = useState<number | null>(null);

  // Cycle through messages
  useEffect(() => {
    if (!isLoading || messages.length <= 1) return;

    const intervalId = setInterval(() => {
      setCurrentMessageIndex((prevIndex) => (prevIndex + 1) % messages.length);
    }, cycleSpeed);

    return () => clearInterval(intervalId);
  }, [isLoading, messages, cycleSpeed]);

  // Handle showing/hiding with minimum display time
  useEffect(() => {
    // Set start time when loading begins
    if (isLoading && startTime === null) {
      setStartTime(Date.now());
      setVisible(true);
    }

    // Handle transition to not loading
    if (!isLoading && startTime !== null) {
      const elapsedTime = Date.now() - startTime;

      if (elapsedTime < minDisplayTime) {
        // Keep showing for minimum display time
        const remainingTime = minDisplayTime - elapsedTime;
        const timerId = setTimeout(() => {
          setVisible(false);
          if (onLoadingComplete) onLoadingComplete();
        }, remainingTime);

        return () => clearTimeout(timerId);
      } else {
        // Min display time already elapsed, hide immediately
        setVisible(false);
        if (onLoadingComplete) onLoadingComplete();
      }
    }
  }, [isLoading, startTime, minDisplayTime, onLoadingComplete]);

  // Reset start time when loading state changes from false to true
  useEffect(() => {
    if (isLoading) {
      setStartTime(Date.now());
      setVisible(true);
    }
  }, [isLoading]);

  if (!visible) return null;

  return (
    <div className="fixed inset-0 z-50 flex flex-col items-center justify-center bg-background/95 backdrop-blur-sm transition-all duration-300">
      <div className="flex flex-col items-center gap-4 text-center p-6">
        <div className="mb-8">
          <FuturisticLoader size={180} />
        </div>
        <div className="space-y-2">
          <p className="text-2xl font-medium text-foreground min-h-[32px] transition-opacity duration-300">
            {messages[currentMessageIndex]}
          </p>
        </div>
      </div>
    </div>
  );
};
